import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  FiSearch, 
  FiFilter, 
  FiChevronLeft, 
  FiChevronRight, 
  FiChevronsLeft, 
  FiChevronsRight,
  FiEye,
  FiEdit,
  FiTrash2,
  FiMoreVertical,
  FiArrowUp,
  FiArrowDown,
  FiDownload,
  FiShare2,
  FiRefreshCw,
  FiSend,
  FiBarChart2,
  FiSettings
} from 'react-icons/fi';

const UniversalTable = ({
  // Data and Structure
  data = [],
  columns = [],
  
  // Loading and States
  loading = false,
  loadingMessage = "Loading...",
  
  // Features
  searchable = true,
  filterable = true,
  sortable = true,
  pagination = true,
  exportable = false,
  refreshable = false,
  
  // Pagination
  pageSize = 10,
  pageSizeOptions = [5, 10, 25, 50, 100],
  
  // Actions
  actions = [],
  bulkActions = [],
  onRowClick = null,
  onActionClick = null,
  onBulkActionClick = null,
  onRefresh = null,
  onExport = null,
  
  // Customization
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  className = "",
  style = {},
  
  // Table Styling
  striped = true,
  hover = true,
  bordered = false,
  responsive = true,
  size = "md", // sm, md, lg
  variant = "default", // default, primary, secondary, success, danger, warning, info, light, dark
  compact = false,
  showHeader = true,
  stickyHeader = false,
  
  // Selection
  selectable = false,
  selectedRows = [],
  onSelectionChange = null,
  
  // Custom Renderers
  customEmptyState = null,
  customLoadingState = null,
  
  // Advanced Features
  virtualScrolling = false,
  maxHeight = null,
  showRowNumbers = false,
  showColumnFilters = true,
  
  // Accessibility
  ariaLabel = "Data table",
  ariaDescribedBy = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [selectedItems, setSelectedItems] = useState(selectedRows);
  const [pageSizeState, setPageSizeState] = useState(pageSize);

  // Filter and search data
  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply search
    if (searchTerm && searchable) {
      filtered = filtered.filter(row =>
        columns.some(column => {
          const value = row[column.key];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        filtered = filtered.filter(row => {
          const cellValue = row[key];
          return cellValue && cellValue.toString().toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    return filtered;
  }, [data, searchTerm, filters, columns, searchable]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortable) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();

      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig, sortable]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSizeState;
    const endIndex = startIndex + pageSizeState;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSizeState, pagination]);

  // Calculate pagination info
  const totalPages = Math.ceil(filteredData.length / pageSizeState);
  const totalItems = filteredData.length;
  const startItem = (currentPage - 1) * pageSizeState + 1;
  const endItem = Math.min(currentPage * pageSizeState, totalItems);

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  // Handle search change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSizeState(newSize);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleRowSelection = (row, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedItems, row];
    } else {
      newSelection = selectedItems.filter(item => item !== row);
    }
    setSelectedItems(newSelection);
    onSelectionChange && onSelectionChange(newSelection);
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    const newSelection = isSelected ? [...paginatedData] : [];
    setSelectedItems(newSelection);
    onSelectionChange && onSelectionChange(newSelection);
  };

  // Get table size class
  const getTableSizeClass = () => {
    switch (size) {
      case 'sm': return 'table-sm';
      case 'lg': return 'table-lg';
      default: return '';
    }
  };

  // Get table variant class
  const getTableVariantClass = () => {
    switch (variant) {
      case 'primary': return 'table-primary';
      case 'secondary': return 'table-secondary';
      case 'success': return 'table-success';
      case 'danger': return 'table-danger';
      case 'warning': return 'table-warning';
      case 'info': return 'table-info';
      case 'light': return 'table-light';
      case 'dark': return 'table-dark';
      default: return '';
    }
  };

  // Render cell content
  const renderCellContent = (row, column, rowIndex) => {
    const value = row[column.key];
    
    if (column.render) {
      return column.render(value, row, rowIndex);
    }
    
    if (column.type === 'status') {
      const statusConfig = {
        active: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        inactive: { bg: '#6b7280', text: '#ffffff', icon: '○' },
        pending: { bg: '#f59e0b', text: '#ffffff', icon: '⏳' },
        completed: { bg: '#3b82f6', text: '#ffffff', icon: '✓' },
        failed: { bg: '#ef4444', text: '#ffffff', icon: '✗' },
        sent: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        sending: { bg: '#8b5cf6', text: '#ffffff', icon: '↗' },
        draft: { bg: '#6b7280', text: '#ffffff', icon: '📝' },
        success: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        error: { bg: '#ef4444', text: '#ffffff', icon: '✗' },
        warning: { bg: '#f59e0b', text: '#ffffff', icon: '⚠' },
        info: { bg: '#3b82f6', text: '#ffffff', icon: 'ℹ' },
        approved: { bg: '#10b981', text: '#ffffff', icon: '✓' },
        rejected: { bg: '#ef4444', text: '#ffffff', icon: '✗' },
        processing: { bg: '#3b82f6', text: '#ffffff', icon: '⚙' }
      };
      
      const config = statusConfig[value?.toLowerCase()] || statusConfig.inactive;
      
      return (
        <span 
          className="badge badge-pill"
          style={{
            background: `linear-gradient(135deg, ${config.bg} 0%, ${config.bg}dd 100%)`,
            color: config.text,
            padding: compact ? '4px 10px' : '6px 12px',
            borderRadius: '20px',
            fontSize: compact ? '0.7rem' : '0.75rem',
            fontWeight: '600',
            textTransform: 'capitalize',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span style={{ fontSize: '0.8em' }}>{config.icon}</span>
          {value}
        </span>
      );
    }
    
    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : '-';
    }
    
    if (column.type === 'datetime') {
      return value ? new Date(value).toLocaleString() : '-';
    }
    
    if (column.type === 'number') {
      return value ? value.toLocaleString() : '0';
    }
    
    if (column.type === 'currency') {
      return value ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value) : '$0.00';
    }
    
    if (column.type === 'email') {
      return (
        <span style={{ 
          color: '#007bff', 
          textDecoration: 'none',
          fontSize: compact ? '0.85rem' : '0.9rem'
        }}>
          {value || '-'}
        </span>
      );
    }
    
    if (column.type === 'name') {
      return (
        <span style={{ 
          fontWeight: '500',
          fontSize: compact ? '0.85rem' : '0.9rem'
        }}>
          {value || '-'}
        </span>
      );
    }
    
    if (column.type === 'boolean') {
      return (
        <span 
          className={`badge badge-${value ? 'success' : 'secondary'}`}
          style={{
            padding: compact ? '2px 6px' : '4px 8px',
            fontSize: compact ? '0.7rem' : '0.75rem'
          }}
        >
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    
    if (column.type === 'image') {
      return value ? (
        <img 
          src={value} 
          alt=""
          style={{
            width: compact ? '30px' : '40px',
            height: compact ? '30px' : '40px',
            borderRadius: '4px',
            objectFit: 'cover'
          }}
        />
      ) : '-';
    }
    
    // Handle objects and arrays
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.length > 0 ? `${value.length} items` : 'Empty';
      }
      return JSON.stringify(value);
    }
    
    return value || '-';
  };

  // Render action buttons
  const renderActions = (row, rowIndex) => {
    if (!actions || actions.length === 0) return null;

    // Filter actions based on show condition
    const visibleActions = actions.filter(action => {
      if (typeof action.show === 'function') {
        return action.show(row);
      }
      return action.show !== false; // Default to true if not specified
    });

    if (visibleActions.length === 0) return null;

    return (
      <div className="btn-group" role="group" style={{ gap: '4px' }}>
        {visibleActions.map((action, index) => {
          const variantColors = {
            primary: { bg: '#3b82f6', hover: '#2563eb', text: '#ffffff' },
            secondary: { bg: '#6b7280', hover: '#4b5563', text: '#ffffff' },
            success: { bg: '#10b981', hover: '#059669', text: '#ffffff' },
            danger: { bg: '#ef4444', hover: '#dc2626', text: '#ffffff' },
            warning: { bg: '#f59e0b', hover: '#d97706', text: '#ffffff' },
            info: { bg: '#06b6d4', hover: '#0891b2', text: '#ffffff' },
            light: { bg: '#f3f4f6', hover: '#e5e7eb', text: '#374151' },
            dark: { bg: '#374151', hover: '#1f2937', text: '#ffffff' }
          };
          
          const colors = variantColors[action.variant] || variantColors.secondary;
          
          return (
            <button
              key={index}
              type="button"
              className="btn btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                onActionClick && onActionClick(action.key, row, rowIndex);
              }}
              title={action.title}
              disabled={action.disabled && action.disabled(row)}
              style={{ 
                padding: compact ? '4px 8px' : '6px 12px',
                fontSize: compact ? '0.75rem' : '0.8rem',
                border: 'none',
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${colors.bg} 0%, ${colors.hover} 100%)`,
                color: colors.text,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                if (!action.disabled || !action.disabled(row)) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              {action.icon || <FiMoreVertical />}
              {action.label && <span>{action.label}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  // Render bulk actions
  const renderBulkActions = () => {
    if (!bulkActions || bulkActions.length === 0 || selectedItems.length === 0) return null;

    return (
      <div 
        className="d-flex align-items-center"
        style={{
          padding: '10px 15px',
          backgroundColor: '#e3f2fd',
          borderBottom: '1px solid #dee2e6',
          gap: '10px'
        }}
      >
        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
          {selectedItems.length} item(s) selected
        </span>
        {bulkActions.map((action, index) => (
          <button
            key={index}
            type="button"
            className={`btn btn-${action.variant || 'primary'} btn-sm`}
            onClick={() => onBulkActionClick && onBulkActionClick(action.key, selectedItems)}
            disabled={action.disabled && action.disabled(selectedItems)}
            style={{ fontSize: '0.8rem' }}
          >
            {action.icon}
            {action.label}
          </button>
        ))}
      </div>
    );
  };

  // Custom loading state
  if (loading) {
    if (customLoadingState) {
      return customLoadingState;
    }
    
    return (
      <div 
        className="d-flex justify-content-center align-items-center" 
        style={{ 
          height: '200px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">{loadingMessage}</span>
        </div>
        <span style={{ marginLeft: '10px', color: '#6c757d' }}>{loadingMessage}</span>
      </div>
    );
  }

  return (
    <div 
      className={`universal-table ${className}`} 
      style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid #e9ecef',
        ...style
      }}
    >
      {/* Header Controls */}
      <div 
        // style={{ 
        //   padding: '20px',
        //   background: 'transparent',
        //   borderBottom: 'none',
        //   display: 'flex',
        //   justifyContent: 'space-between',
        //   alignItems: 'center',
        //   flexWrap: 'wrap',
        //   gap: '15px',
        //   color: 'white'
        // }}
      >
        {/* Left side - Search and Filters */}
        <div className="d-flex align-items-center" style={{ gap: '15px', flexWrap: 'wrap' }}>
          {searchable && (
            <div className="input-group" style={{ minWidth: '280px' }}>
              <div className="input-group-prepend">
                <span 
                  className="input-group-text"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: 'none',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <FiSearch style={{ color: '#667eea' }} />
                </span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  border: 'none',
                  borderLeft: 'none',
                  fontSize: compact ? '0.85rem' : '0.9rem',
                  borderRadius: '0 8px 8px 0',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
          )}
          
          {filterable && showColumnFilters && (
            <div className="d-flex" style={{ gap: '10px', flexWrap: 'wrap' }}>
              {columns.filter(col => col.filterable !== false).map((column, index) => (
                <select
                  key={index}
                  className="form-control form-control-sm"
                  value={filters[column.key] || 'all'}
                  onChange={(e) => handleFilterChange(column.key, e.target.value)}
                  style={{
                    fontSize: compact ? '0.8rem' : '0.85rem',
                    border: '1px solid #ced4da',
                    minWidth: '120px'
                  }}
                >
                  <option value="all">All {column.title}</option>
                  {[...new Set(data.map(row => row[column.key]))].map(value => {
                    const displayValue = typeof value === 'object' && value !== null 
                      ? JSON.stringify(value) 
                      : value || 'Empty';
                    return (
                      <option key={displayValue} value={displayValue}>{displayValue}</option>
                    );
                  })}
                </select>
              ))}
            </div>
          )}
        </div>

        {/* Right side - Actions */}
        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
          {refreshable && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onRefresh}
              title="Refresh"
            >
              <FiRefreshCw />
            </button>
          )}
          
          {exportable && (
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={onExport}
              title="Export"
            >
              <FiDownload />
            </button>
          )}
          
          {pagination && pageSizeOptions.length > 1 && (
            <div className="d-flex align-items-center" style={{ gap: '5px' }}>
              <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>Show:</span>
              <select
                className="form-control form-control-sm"
                value={pageSizeState}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={{
                  fontSize: '0.8rem',
                  width: '70px',
                  border: '1px solid #ced4da'
                }}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {renderBulkActions()}

      {/* Table */}
      <div 
        className={responsive ? "table-responsive" : ""} 
        style={{ 
          maxHeight: maxHeight || (stickyHeader ? '400px' : 'none'),
          overflowY: maxHeight || stickyHeader ? 'auto' : 'visible'
        }}
      >
        <table 
          className={`table ${getTableSizeClass()} ${getTableVariantClass()} ${
            striped ? 'table-striped' : ''
          } ${hover ? 'table-hover' : ''} ${bordered ? 'table-bordered' : ''} mb-0`}
          style={{
            marginBottom: '0',
            fontSize: compact ? '0.85rem' : '0.9rem'
          }}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
        >
          {showHeader && (
            <thead 
              className="thead-dark"
              style={{
                position: stickyHeader ? 'sticky' : 'static',
                top: '0',
                zIndex: '10',
                background: 'transparent',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <tr>
                {selectable && (
                  <th style={{ width: '40px', padding: compact ? '8px' : '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ transform: 'scale(0.9)' }}
                    />
                  </th>
                )}
                
                {showRowNumbers && (
                  <th style={{ width: '50px', padding: compact ? '8px' : '12px' }}>#</th>
                )}
                
                {columns.map((column, index) => (
                  <th
                    key={index}
                    scope="col"
                    style={{
                      cursor: sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      position: 'relative',
                      padding: compact ? '8px 12px' : '12px 15px',
                      fontSize: compact ? '0.8rem' : '0.85rem',
                      fontWeight: '600',
                      borderBottom: '2px solid #495057',
                      minWidth: column.minWidth || 'auto',
                      maxWidth: column.maxWidth || 'none'
                    }}
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{column.title}</span>
                      {sortable && sortConfig.key === column.key && (
                        <span style={{ fontSize: '0.8rem', marginLeft: '5px' }}>
                          {sortConfig.direction === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {actions && actions.length > 0 && (
                  <th 
                    scope="col" 
                    style={{ 
                      width: '120px',
                      padding: compact ? '8px 12px' : '12px 15px',
                      fontSize: compact ? '0.8rem' : '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
          )}
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: selectedItems.includes(row) ? '#e3f2fd' : 'transparent'
                  }}
                  onClick={() => onRowClick && onRowClick(row, rowIndex)}
                >
                  {selectable && (
                    <td style={{ padding: compact ? '8px' : '12px', verticalAlign: 'middle' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(row)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRowSelection(row, e.target.checked);
                        }}
                        style={{ transform: 'scale(0.9)' }}
                      />
                    </td>
                  )}
                  
                  {showRowNumbers && (
                    <td style={{ padding: compact ? '8px' : '12px', verticalAlign: 'middle' }}>
                      {startItem + rowIndex}
                    </td>
                  )}
                  
                  {columns.map((column, colIndex) => (
                    <td 
                      key={colIndex}
                      style={{
                        padding: compact ? '8px 12px' : '12px 15px',
                        verticalAlign: 'middle',
                        borderTop: '1px solid #dee2e6',
                        minWidth: column.minWidth || 'auto',
                        maxWidth: column.maxWidth || 'none',
                        overflow: column.maxWidth ? 'hidden' : 'visible',
                        textOverflow: column.maxWidth ? 'ellipsis' : 'initial',
                        whiteSpace: column.maxWidth ? 'nowrap' : 'normal'
                      }}
                    >
                      {renderCellContent(row, column, rowIndex)}
                    </td>
                  ))}
                  {actions && actions.length > 0 && (
                    <td 
                      style={{
                        padding: compact ? '8px 12px' : '12px 15px',
                        verticalAlign: 'middle',
                        borderTop: '1px solid #dee2e6'
                      }}
                    >
                      {renderActions(row, rowIndex)}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td 
                  colSpan={columns.length + (actions && actions.length > 0 ? 1 : 0) + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)}
                  className="text-center text-muted"
                  style={{ 
                    padding: '2rem',
                    fontSize: compact ? '0.85rem' : '0.9rem',
                    color: '#6c757d'
                  }}
                >
                  {customEmptyState || (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <FiSearch style={{ fontSize: '2rem', marginBottom: '10px', opacity: 0.5 }} />
                      {emptyMessage}
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div 
          className="d-flex justify-content-between align-items-center"
          style={{
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #dee2e6'
          }}
        >
          <div 
            className="text-muted"
            style={{ fontSize: compact ? '0.8rem' : '0.85rem' }}
          >
            Showing {startItem} to {endItem} of {totalItems} entries
          </div>
          
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronsLeft />
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronLeft />
                </button>
              </li>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: compact ? '4px 8px' : '6px 12px',
                        fontSize: compact ? '0.75rem' : '0.8rem'
                      }}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronRight />
                </button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: compact ? '4px 8px' : '6px 12px',
                    fontSize: compact ? '0.75rem' : '0.8rem'
                  }}
                >
                  <FiChevronsRight />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default UniversalTable;
